import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { ImageSize, getImagePathWithSize, useFirebaseImage } from '../Helpers/ImageLoader';
import Marker from './Marker';
import './HoverPreview.css';

/** Longest side of a hover preview image, shrunk to fit a small window. */
const MAX_PREVIEW_SIZE = 560;
const OPEN_DELAY_MS = 300;
const GAP = 8;
const VIEWPORT_MARGIN = 8;
/** The card's padding on each side (HoverPreview.css). */
const CARD_PADDING = 6;
const MARKER_SIZE = 16;

/** The longest side that fits the window with the card's margin and padding. */
const fittedPreviewSize = (): number => Math.max(120, Math.min(
    MAX_PREVIEW_SIZE,
    window.innerWidth - 2 * (VIEWPORT_MARGIN + CARD_PADDING),
    window.innerHeight - 2 * (VIEWPORT_MARGIN + CARD_PADDING),
));

interface HoverPreviewProps {
    /** The enlargement at the given longest side, rendered only while the preview is open. */
    renderPreview: (size: number) => React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

/**
 * Shows an enlargement beside its child after a short hover. Mouse only: on touch screens
 * (no `hover: hover`) nothing happens, so a tap keeps doing what it did. The card is portaled
 * to body so scrolling lists never clip it, ignores the pointer, and closes on leave, click or scroll.
 */
const HoverPreview: React.FC<HoverPreviewProps> = ({ renderPreview, children, className }) => {
    const anchorRef = useRef<HTMLSpanElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<number | null>(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

    const clearTimer = () => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const close = useCallback(() => {
        clearTimer();
        setOpen(false);
        setPosition(null);
    }, []);

    const handleEnter = () => {
        if (!window.matchMedia?.('(hover: hover)').matches) return;
        clearTimer();
        timerRef.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    };

    useEffect(() => clearTimer, []);

    useEffect(() => {
        if (!open) return;
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open, close]);

    // Place the card once its real size is known (it changes as the image loads):
    // right of the anchor, flipped left when it would overflow, vertically centred and clamped.
    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            const anchor = anchorRef.current?.getBoundingClientRect();
            const card = cardRef.current;
            if (!anchor || !card) return;
            const { offsetWidth: width, offsetHeight: height } = card;
            let left = anchor.right + GAP;
            if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
                left = Math.max(VIEWPORT_MARGIN, anchor.left - GAP - width);
            }
            const centred = anchor.top + anchor.height / 2 - height / 2;
            const top = Math.min(
                Math.max(VIEWPORT_MARGIN, centred),
                Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - height),
            );
            setPosition(prev => (prev && prev.left === left && prev.top === top ? prev : { left, top }));
        };
        place();
        const observer = new ResizeObserver(place);
        if (cardRef.current) observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, [open]);

    return (
        <span
            ref={anchorRef}
            className={`hover-preview-anchor ${className ?? ''}`}
            onMouseEnter={handleEnter}
            onMouseLeave={close}
            onMouseDown={close}
        >
            {children}
            {open && createPortal(
                <div
                    ref={cardRef}
                    className="hover-preview-card"
                    style={position ?? { left: 0, top: 0, visibility: 'hidden' }}
                    aria-hidden="true"
                >
                    {renderPreview(fittedPreviewSize())}
                </div>,
                document.body,
            )}
        </span>
    );
};

interface HoverPreviewImageProps {
    storagePath: string;
    /** The image's own pixel size; sets the aspect ratio and the marker's scale. */
    width: number;
    height: number;
    size: number;
    /** A position on the image in its own pixels, drawn as the red marker. */
    marker?: { x: number; y: number };
}

/**
 * An image at preview size with its own aspect ratio (scaled medium, falling back to the original).
 * A spinner holds its box while it loads; then the image fades in.
 */
export const HoverPreviewImage: React.FC<HoverPreviewImageProps> = ({ storagePath, width: imageWidth, height: imageHeight, size, marker }) => {
    const scaled = useFirebaseImage(getImagePathWithSize(storagePath, ImageSize.SCALED_MEDIUM));
    const full = useFirebaseImage(scaled.hasError ? storagePath : null);
    const current = scaled.hasError ? full : scaled;
    const ready = !current.isLoading && !current.hasError;
    const [shown, setShown] = useState(false);

    const aspect = imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : 1;
    const width = aspect >= 1 ? size : size * aspect;
    const height = aspect >= 1 ? size / aspect : size;

    return (
        <div className="hover-preview-image" style={{ width, height }}>
            {!shown && !current.hasError && (
                <Spinner animation="border" size="sm" className="hover-preview-spinner" />
            )}
            {current.hasError && <span className="hover-preview-missing">Foto saknas</span>}
            {ready && (
                <img
                    src={current.src}
                    alt=""
                    className={shown ? 'shown' : undefined}
                    onLoad={() => setShown(true)}
                />
            )}
            {shown && marker && imageWidth > 0 && imageHeight > 0 && (
                <Marker position={{
                    left: `${(marker.x / imageWidth) * 100}%`,
                    top: `${(marker.y / imageHeight) * 100}%`,
                    width: `${MARKER_SIZE}px`,
                    height: `${MARKER_SIZE}px`,
                }} />
            )}
        </div>
    );
};

export default HoverPreview;
